import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, CheckCircle2, AlertTriangle,
  ShieldCheck, X,
  Scale, Droplet, Dumbbell,
  Frown, Baby, Cookie, LineChart, MessageCircle,
  Phone, Calendar,
  Stethoscope, ClipboardList, TrendingUp,
  HeartHandshake, Check, Sparkles, Tag, Percent, Lock,
} from 'lucide-react';
import Logo from '../components/Logo';
import { useReferralCta } from '../hooks/useReferralCta';
import { LANDING_CTA_URL } from '../utils/ctaUrl';
import {
  computeBmi, computeEligibility, recommendBottles, toQuizPayload,
} from '../utils/eligibility';
import {
  trackAssessmentStart, trackAssessmentStarted,
  trackConsentAccept, trackAssessmentComplete,
  trackResultView, trackCtaClick,
} from '../utils/analytics';
import { BRAND_NAME } from '../utils/brand';
import { whatsappUrl } from '../utils/whatsapp';
import api from '../api/client';

const SESSION_KEY = 'mdai_intake_state_v3'; // bumped — dob + assessmentId
const CONSENT_VERSION = '1.0';              // mirror sensitive-data-consent page

// Care-team WhatsApp (per client PDF 2026-07-12) — hard-coded so block screens
// route correctly even if REACT_APP_WHATSAPP_NUMBER is unset in the environment.
const CARE_WHATSAPP_NUMBER = '601120801876';
function careWhatsappUrl(prefill) {
  const q = prefill ? `?text=${encodeURIComponent(prefill)}` : '';
  return `https://wa.me/${CARE_WHATSAPP_NUMBER}${q}`;
}

// Assessment reference used in WhatsApp prefill so care team can look up state.
function newAssessmentId() {
  const t = Date.now().toString(36).toUpperCase();
  const r = Math.random().toString(36).slice(2, 8).toUpperCase();
  return `MYD-${t}-${r}`;
}

// Compute years from a YYYY-MM-DD date-of-birth string. Returns null if invalid.
function ageFromDob(dob) {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age;
}

// Wizard step ids. The wordy "Before we start" consent gate was removed per
// client PDF 2026-07-12 — consent is captured implicitly by a compact
// "By continuing…" disclaimer under the final CTA on the result page.
// The two `*Block` steps are terminal — reached only when the user is not
// eligible to continue the online pathway.
const STEPS = [
  'welcome',          // goal selection
  'basic',            // date of birth
  'underAgeBlock',    // terminal: age < 18
  'metrics',          // height + weight + BMI
  'lowBmiBlock',      // terminal: BMI < 18.5
  'hardContras',      // absolute contraindications
  'highRisk',         // high-risk medical history
  'medications',      // current medications
  'challenges',       // multi-select challenges
  'targetLoss',       // target weight loss bracket
  'eatingDisorder',   // eating-disorder history
  'surgery',          // upcoming surgery / anaesthesia screening (§5)
  'urgent',           // red-flag symptoms (§5)
  'result',           // spec §5 result routing: 4 categories
];

const GOAL_OPTIONS = [
  { value: 'WEIGHT_LOSS',         label: 'Lose weight',          icon: Scale },
  { value: 'BLOOD_SUGAR_CONTROL', label: 'Control blood sugar',  icon: Droplet },
  { value: 'BODY_SHAPING',        label: 'Reshape my body',      icon: Dumbbell },
];

const HARD_CONTRA_Q = [
  { key: 'mtcHistory',       label: 'Personal or family history of medullary thyroid carcinoma (MTC)?' },
  { key: 'menType2',         label: 'Diagnosed with multiple endocrine neoplasia type 2 (MEN-2)?' },
  { key: 'glp1Allergy',      label: 'Severe allergy to GLP-1 medications (e.g. semaglutide, tirzepatide)?' },
  { key: 'pregnantOrTrying', label: 'Pregnant, planning a pregnancy, or breastfeeding?' },
];

const HIGH_RISK_Q = [
  { key: 'pancreatitisHistory', label: 'History of pancreatitis?' },
  { key: 'gallstones',          label: 'Gallstones or gallbladder inflammation?' },
  { key: 'severeGastric',       label: 'Severe gastric issues or gastroparesis?' },
  { key: 'kidneyProblems',      label: 'Known kidney problems?' },
  { key: 'chronicVomiting',     label: 'Chronic vomiting or ongoing digestive issues?' },
];

const MEDICATION_Q = [
  { key: 'diabetesMedication',    label: 'On any diabetes medication (insulin, etc.)?' },
  { key: 'oralGlucoseMedication', label: 'On any oral glucose-lowering medication?' },
  { key: 'weightLossSupplements', label: 'Currently using any weight-loss supplements?' },
];

const SURGERY_Q = [
  { key: 'upcomingSurgery',  label: 'Do you have any surgery or endoscopy scheduled in the next 8 weeks?' },
  { key: 'deepSedation',     label: 'Will it involve anaesthesia or deep sedation?' },
];

const URGENT_Q = [
  { key: 'severeAbdominalPain', label: 'Severe or persistent abdominal pain (especially radiating to your back)?' },
  { key: 'repeatedVomiting',    label: 'Repeated vomiting or inability to keep fluids down?' },
  { key: 'allergicReaction',    label: 'Signs of allergic reaction (rash, face/throat swelling)?' },
  { key: 'breathingDifficulty', label: 'Difficulty breathing?' },
  { key: 'chestPain',           label: 'Chest pain?' },
  { key: 'faintingOrConfusion', label: 'Fainting, severe dizziness or confusion?' },
  { key: 'severeDehydration',   label: 'Signs of severe dehydration?' },
];

const initialState = {
  assessmentId: '',
  consent: {
    accepted: false,
    version: CONSENT_VERSION,
    acceptedAt: null,
  },
  basic: { dob: '', heightCm: '', weightKg: '' },
  lifestyle: { goal: 'WEIGHT_LOSS', challenges: [], targetLossKg: '', eatingDisorderHistory: null },
  contraindications: { mtcHistory: null, menType2: null, glp1Allergy: null, pregnantOrTrying: null },
  highRiskFlags: { pancreatitisHistory: null, gallstones: null, severeGastric: null, kidneyProblems: null, chronicVomiting: null },
  medications: { diabetesMedication: null, oralGlucoseMedication: null, weightLossSupplements: null },
  surgery: { upcomingSurgery: null, deepSedation: null },
  urgentSymptoms: {
    severeAbdominalPain: null, repeatedVomiting: null, allergicReaction: null,
    breathingDifficulty: null, chestPain: null, faintingOrConfusion: null, severeDehydration: null,
  },
};

function loadState() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? { ...initialState, ...JSON.parse(raw) } : initialState;
  } catch {
    return initialState;
  }
}

function persistState(state) {
  try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(state)); } catch { /* ignore */ }
}

function clearState() {
  try { sessionStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
}

function encodePayload(payload) {
  try {
    const json = JSON.stringify(payload);
    const b64 = typeof window !== 'undefined' && window.btoa
      ? window.btoa(unescape(encodeURIComponent(json)))
      : '';
    return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  } catch {
    return '';
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Layout: wizard chrome shared by every step.
// ────────────────────────────────────────────────────────────────────────────

function StepShell({
  stepNumber, totalSteps, title, subtitle, children,
  onBack, onNext, nextLabel, nextDisabled, hideNext, footerExtra,
}) {
  return (
    <div className="min-h-screen flex flex-col bg-canvas">
      <div className="sticky top-0 z-20 bg-canvas/95 backdrop-blur border-b border-ink-100">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/" aria-label="Exit assessment" className="shrink-0 text-ink-500 hover:text-ink-900">
            <X size={20} />
          </Link>
          <div className="flex-1 flex items-center justify-center">
            <Logo height={32} />
          </div>
          <div className="shrink-0 text-xs text-ink-500 tabular-nums" aria-live="polite">
            {stepNumber} / {totalSteps}
          </div>
        </div>
        <div className="h-1 bg-ink-100 max-w-2xl mx-auto">
          <div
            className="h-full bg-brand-600 transition-all duration-300"
            style={{ width: `${(stepNumber / totalSteps) * 100}%` }}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-4 pt-6 pb-32">
          {title && <h1 className="text-2xl sm:text-3xl font-bold text-ink-900 leading-tight">{title}</h1>}
          {subtitle && <p className="mt-2 text-ink-600 text-sm sm:text-base leading-relaxed">{subtitle}</p>}
          <div className="mt-6">{children}</div>
          {footerExtra}
        </div>
      </div>

      <div className="sticky bottom-0 z-20 bg-canvas/95 backdrop-blur border-t border-ink-100">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-3 text-sm font-medium text-ink-700 hover:bg-ink-100 transition-colors"
            >
              <ArrowLeft size={16} /> Back
            </button>
          ) : <span className="w-1" />}

          {!hideNext && (
            <button
              type="button"
              onClick={onNext}
              disabled={nextDisabled}
              className={[
                'flex-1 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3.5 text-sm font-semibold transition-colors',
                nextDisabled
                  ? 'bg-ink-200 text-ink-400 cursor-not-allowed'
                  : 'bg-brand-600 text-white hover:bg-brand-700'
              ].join(' ')}
            >
              {nextLabel || 'Continue'} <ArrowRight size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function YesNoCard({ label, value, onChange, sub }) {
  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-4 mb-3">
      <div className="text-ink-900 font-medium leading-snug">{label}</div>
      {sub && <div className="text-ink-500 text-xs mt-1">{sub}</div>}
      <div className="mt-3 grid grid-cols-2 gap-2">
        {[{ v: false, label: 'No' }, { v: true, label: 'Yes' }].map((opt) => {
          const active = value === opt.v;
          return (
            <button
              key={String(opt.v)}
              type="button"
              onClick={() => onChange(opt.v)}
              className={[
                'rounded-xl py-3 text-sm font-semibold transition-colors',
                active
                  ? (opt.v ? 'bg-brand-600 text-white' : 'bg-ink-900 text-white')
                  : 'bg-ink-50 text-ink-900 hover:bg-ink-100'
              ].join(' ')}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ChoiceCard({ label, icon: Icon, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'w-full flex items-center gap-3 rounded-2xl border-2 p-4 sm:p-5 text-left transition-colors',
        active ? 'border-brand-600 bg-brand-50' : 'border-ink-200 bg-white hover:border-ink-300'
      ].join(' ')}
    >
      {Icon && (
        <span
          className={[
            'shrink-0 inline-flex items-center justify-center rounded-xl w-10 h-10',
            active ? 'bg-brand-600 text-white' : 'bg-ink-100 text-ink-700'
          ].join(' ')}
          aria-hidden
        >
          <Icon size={20} />
        </span>
      )}
      <span className="font-medium text-ink-900">{label}</span>
      <CheckCircle2
        size={20}
        className={`ml-auto shrink-0 ${active ? 'text-brand-600' : 'text-ink-300'}`}
      />
    </button>
  );
}

function NumberField({ label, sub, value, onChange, suffix, inputMode = 'numeric', min, max, error }) {
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-ink-900">{label}</label>
      {sub && <div className="text-xs text-ink-500 mt-0.5">{sub}</div>}
      <div className={[
        'mt-2 flex items-center rounded-xl border bg-white px-3',
        error ? 'border-rose-400' : 'border-ink-200 focus-within:border-brand-600'
      ].join(' ')}>
        <input
          type="number"
          inputMode={inputMode}
          min={min}
          max={max}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 py-3 bg-transparent outline-none text-base text-ink-900"
        />
        {suffix && <span className="text-ink-500 text-sm">{suffix}</span>}
      </div>
      {error && <div className="text-xs text-rose-600 mt-1">{error}</div>}
    </div>
  );
}

// Hybrid DOB field: user can either TYPE the date (dd/mm/yyyy, auto-masked)
// or tap the calendar icon to open the native date picker. `value` is the
// ISO string (YYYY-MM-DD) that the rest of the app expects; `onChange` is
// only called with a valid, in-range ISO date (or '' to clear).
const DOB_MIN = '1920-01-01';
function isoToDisplay(iso) {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return '';
  return `${m[3]}/${m[2]}/${m[1]}`;
}
function maskDobInput(raw) {
  // Strip non-digits, insert '/' at positions 2 and 4, cap at 8 digits.
  const digits = String(raw || '').replace(/\D+/g, '').slice(0, 8);
  const parts = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)]
    .filter(Boolean);
  return parts.join('/');
}
function parseDisplayToIso(display) {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec((display || '').trim());
  if (!m) return { iso: '', error: '' };
  const [, dd, mm, yyyy] = m;
  const d = Number(dd), mo = Number(mm), y = Number(yyyy);
  if (y < 1920) return { iso: '', error: 'Year must be 1920 or later.' };
  if (mo < 1 || mo > 12) return { iso: '', error: 'Month must be between 01 and 12.' };
  // Verify the calendar day is real (e.g. reject 31/02/2000).
  const date = new Date(Date.UTC(y, mo - 1, d));
  const valid = date.getUTCFullYear() === y
             && date.getUTCMonth() === mo - 1
             && date.getUTCDate() === d;
  if (!valid) return { iso: '', error: 'That date does not exist on the calendar.' };
  // No future dates.
  const today = new Date();
  const todayIso = today.toISOString().slice(0, 10);
  const iso = `${yyyy}-${mm}-${dd}`;
  if (iso > todayIso) return { iso: '', error: 'Date of birth cannot be in the future.' };
  return { iso, error: '' };
}

function DobInput({ value, onChange }) {
  const [display, setDisplay] = React.useState(isoToDisplay(value));
  const [error, setError] = React.useState('');
  const hiddenRef = React.useRef(null);

  // Keep display in sync if `value` changes from outside (e.g. state hydrate).
  React.useEffect(() => {
    setDisplay((current) => {
      const next = isoToDisplay(value);
      return current === next ? current : next;
    });
  }, [value]);

  function onText(e) {
    const masked = maskDobInput(e.target.value);
    setDisplay(masked);
    // Only commit / validate when the field is either empty or fully filled.
    if (masked === '') {
      setError('');
      if (value) onChange('');
      return;
    }
    if (masked.length === 10) {
      const { iso, error: err } = parseDisplayToIso(masked);
      setError(err);
      if (iso && iso !== value) onChange(iso);
    } else {
      // Partial — clear any prior error while typing, don't touch parent state.
      setError('');
    }
  }

  function openPicker() {
    const el = hiddenRef.current;
    if (!el) return;
    if (typeof el.showPicker === 'function') {
      try { el.showPicker(); return; } catch { /* fall through */ }
    }
    el.focus();
    el.click();
  }

  function onNativeChange(e) {
    const iso = e.target.value;
    setDisplay(isoToDisplay(iso));
    setError('');
    if (iso !== value) onChange(iso);
  }

  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    <div className="mt-2">
      <div className="relative">
        <input
          type="text"
          inputMode="numeric"
          autoComplete="bday"
          placeholder="dd/mm/yyyy"
          value={display}
          onChange={onText}
          maxLength={10}
          aria-label="Date of birth"
          aria-invalid={Boolean(error) || undefined}
          className={[
            'w-full appearance-none rounded-xl border bg-white pl-4 pr-11 py-3 text-base outline-none tracking-wide',
            'text-ink-900 placeholder:text-ink-400',
            error
              ? 'border-rose-400 focus:border-rose-500 focus:ring-2 focus:ring-rose-200'
              : 'border-ink-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-200',
          ].join(' ')}
        />
        <button
          type="button"
          onClick={openPicker}
          aria-label="Open date picker"
          className="absolute inset-y-0 right-2 my-1 px-2 flex items-center text-brand-600 hover:bg-brand-50 rounded-lg"
        >
          <Calendar size={18} />
        </button>
        {/* Hidden native date input — programmatically opened by the icon.
            Kept in the DOM so mobile browsers can render their calendar UI. */}
        <input
          ref={hiddenRef}
          type="date"
          value={value || ''}
          onChange={onNativeChange}
          min={DOB_MIN}
          max={todayIso}
          tabIndex={-1}
          aria-hidden="true"
          className="sr-only"
        />
      </div>
      {error && <div className="text-xs text-rose-600 mt-1.5">{error}</div>}
      {!error && !display && (
        <div className="text-xs text-ink-500 mt-1.5">
          Type dd/mm/yyyy or tap the calendar to pick a date.
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Main wizard
// ────────────────────────────────────────────────────────────────────────────

export default function Intake() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { ctaUrl } = useReferralCta();

  const [state, setState] = useState(loadState);
  const [stepIdx, setStepIdx] = useState(0);
  const contentRef = useRef(null);

  // Fire assessment_start once per session entry. Also mint an assessment ID
  // if the state doesn't already have one — care team references this in
  // WhatsApp flows even when no user account exists.
  useEffect(() => {
    // Fire both the legacy `assessment_start` and the GA4-canonical
    // `assessment_started` (SEO Master §6) so existing GTM triggers keep
    // firing while the new spec is rolled out.
    trackAssessmentStart({ source_page: 'landing', language: navigator?.language || 'en' });
    trackAssessmentStarted({ source_page: 'landing', language: navigator?.language || 'en' });
    setState((s) => (s.assessmentId ? s : { ...s, assessmentId: newAssessmentId() }));
  }, []);

  // Preselect target-loss bracket if landing-page Packages sent ?bottles=
  useEffect(() => {
    const b = Number(params.get('bottles'));
    if (b && !state.lifestyle.targetLossKg) {
      const target = b === 1 ? '3' : b === 2 ? '6' : '10';
      patchLifestyle({ targetLossKg: target });
    }
  }, [params]);

  useEffect(() => { persistState(state); }, [state]);

  useEffect(() => {
    contentRef.current?.scrollTo?.({ top: 0, behavior: 'auto' });
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [stepIdx]);

  const stepName = STEPS[stepIdx];
  // Exclude terminal block screens from the progress counter — they're only
  // reached when the user is ineligible and shouldn't inflate "1 of 14".
  const isBlockStep = stepName === 'underAgeBlock' || stepName === 'lowBmiBlock';
  const totalSteps = STEPS.filter((s) => s !== 'underAgeBlock' && s !== 'lowBmiBlock').length;
  const visibleStepNumber = isBlockStep
    ? stepIdx  // terminal — just show the step it branched from
    : STEPS.slice(0, stepIdx + 1).filter((s) => s !== 'underAgeBlock' && s !== 'lowBmiBlock').length;

  function patchBasic(p)        { setState((s) => ({ ...s, basic: { ...s.basic, ...p } })); }
  function patchLifestyle(p)    { setState((s) => ({ ...s, lifestyle: { ...s.lifestyle, ...p } })); }
  function patchContra(p)       { setState((s) => ({ ...s, contraindications: { ...s.contraindications, ...p } })); }
  function patchHighRisk(p)     { setState((s) => ({ ...s, highRiskFlags: { ...s.highRiskFlags, ...p } })); }
  function patchMeds(p)         { setState((s) => ({ ...s, medications: { ...s.medications, ...p } })); }
  function patchSurgery(p)      { setState((s) => ({ ...s, surgery: { ...s.surgery, ...p } })); }
  function patchUrgent(p)       { setState((s) => ({ ...s, urgentSymptoms: { ...s.urgentSymptoms, ...p } })); }
  function patchConsent(p)      { setState((s) => ({ ...s, consent: { ...s.consent, ...p } })); }

  const bmi = useMemo(
    () => computeBmi(state.basic.heightCm, state.basic.weightKg),
    [state.basic.heightCm, state.basic.weightKg]
  );

  // Result computed only on the result step — fires assessment_complete +
  // result_view exactly once when the result is shown.
  const result = useMemo(() => {
    if (stepName !== 'result') return null;
    const payload = {
      basicInfo: { ...state.basic, bmi },
      contraindications: state.contraindications,
      highRiskFlags: state.highRiskFlags,
      currentMedications: state.medications,
      lifestyle: state.lifestyle,
      surgery: state.surgery,
      urgentSymptoms: state.urgentSymptoms,
    };
    const elig = computeEligibility(payload);
    const reco = recommendBottles(state.lifestyle.targetLossKg);
    return { ...elig, reco };
  }, [stepName, state, bmi]);

  useEffect(() => {
    if (stepName === 'result' && result) {
      trackAssessmentComplete({ source_page: 'landing', result_category: result.category });
      trackResultView({ result_category: result.category });
    }
  }, [stepName, result]);

  // Persist the assessment snapshot to the backend so admin/super-admin can
  // see every completed assessment — including terminal blocks reached
  // before the result step (age <18, BMI <18.5). Idempotent per
  // `assessmentNumber` (backend upserts); silent on failure so a slow API
  // never blocks the visitor from continuing or seeing their result.
  useEffect(() => {
    const isTerminal =
      stepName === 'result' ||
      stepName === 'underAgeBlock' ||
      stepName === 'lowBmiBlock';
    if (!isTerminal) return;
    if (!state.assessmentId) return;

    const resultCategory =
      stepName === 'underAgeBlock' ? 'UNDER_18' :
      stepName === 'lowBmiBlock'   ? 'LOW_BMI'  :
      (result?.status || 'OTHER');

    const body = {
      assessmentNumber: state.assessmentId,
      resultCategory,
      resultReason: result?.reason || '',
      recommendedBottles: result?.reco?.bottles ?? undefined,
      recommendedWeeks:   result?.reco?.weeks   ?? undefined,
      basicInfo: {
        name:     state.basic?.name?.trim() || undefined,
        dob:      state.basic?.dob || undefined,
        age:      state.basic?.age ? Number(state.basic.age) : undefined,
        heightCm: state.basic?.heightCm ? Number(state.basic.heightCm) : undefined,
        weightKg: state.basic?.weightKg ? Number(state.basic.weightKg) : undefined,
        bmi:      typeof bmi === 'number' ? bmi : undefined,
        contactPhone: state.basic?.contactPhone?.trim() || undefined,
        contactEmail: state.basic?.contactEmail?.trim() || undefined,
      },
      payload: toQuizPayload(state),
      sourcePage: 'landing_intake',
    };

    api.post('/assessments', body).catch(() => { /* non-fatal */ });
  }, [stepName, result, state, bmi]);

  // ── Step-gating rules ──────────────────────────────────────────────────────
  const canAdvance = useMemo(() => {
    switch (stepName) {
      case 'welcome':
        return !!state.lifestyle.goal;
      case 'basic':
        return !!state.basic.dob && ageFromDob(state.basic.dob) != null;
      case 'underAgeBlock':
      case 'lowBmiBlock':
        return false;   // terminal — no continue
      case 'metrics':
        return !!(state.basic.heightCm && state.basic.weightKg && bmi);
      case 'hardContras':
        return Object.values(state.contraindications).every((v) => v === true || v === false);
      case 'highRisk':
        return Object.values(state.highRiskFlags).every((v) => v === true || v === false);
      case 'medications':
        return Object.values(state.medications).every((v) => v === true || v === false);
      case 'challenges':
        return true;
      case 'targetLoss':
        return Number(state.lifestyle.targetLossKg) > 0;
      case 'eatingDisorder':
        return state.lifestyle.eatingDisorderHistory === true || state.lifestyle.eatingDisorderHistory === false;
      case 'surgery':
        return Object.values(state.surgery).every((v) => v === true || v === false);
      case 'urgent':
        return Object.values(state.urgentSymptoms).every((v) => v === true || v === false);
      default:
        return true;
    }
  }, [stepName, state, bmi]);

  // Steps that should be skipped unless the guard condition triggers them.
  // Block steps are reached only via explicit routing from `basic`/`metrics`.
  function stepIsSkipped(name) {
    return name === 'underAgeBlock' || name === 'lowBmiBlock';
  }
  function nextStepIndex(fromIdx) {
    const current = STEPS[fromIdx];
    if (current === 'basic') {
      const age = ageFromDob(state.basic.dob);
      if (age != null && age < 18) return STEPS.indexOf('underAgeBlock');
      return STEPS.indexOf('metrics');
    }
    if (current === 'metrics') {
      if (bmi != null && bmi < 18.5) return STEPS.indexOf('lowBmiBlock');
      return STEPS.indexOf('hardContras');
    }
    // Default: advance to the next non-skipped step.
    let i = fromIdx + 1;
    while (i < STEPS.length && stepIsSkipped(STEPS[i])) i += 1;
    return Math.min(STEPS.length - 1, i);
  }
  function prevStepIndex(fromIdx) {
    let i = fromIdx - 1;
    while (i >= 0 && stepIsSkipped(STEPS[i])) i -= 1;
    return Math.max(0, i);
  }
  function onBack() {
    if (stepIdx === 0) { navigate('/'); return; }
    setStepIdx((i) => prevStepIndex(i));
  }
  function onNext() {
    if (!canAdvance) return;
    setStepIdx((i) => nextStepIndex(i));
  }
  function restart() {
    clearState();
    setState({ ...initialState, assessmentId: newAssessmentId() });
    setStepIdx(0);
  }

  function continueToSignup() {
    // Implicit consent captured on Continue — the compact "By continuing…"
    // line under the CTA replaces the old wordy consent gate. Timestamp the
    // acceptance so the audit trail still records who agreed and when.
    const ts = new Date().toISOString();
    patchConsent({ accepted: true, acceptedAt: ts, version: CONSENT_VERSION });
    trackConsentAccept({ consent_version: CONSENT_VERSION });
    trackCtaClick({
      page: 'assessment_result',
      button_label: 'Pay & Book My Consultation',
      destination: 'portal_pay_now',
    });
    // Route straight to the portal's /pay-now shortcut. That page requires
    // auth — logged-out users get bounced through /login?from=/pay-now (the
    // portal's AppLayout preserves the intake query string), signed-in users
    // land immediately. /pay-now then submits the intake, creates the order,
    // and redirects to Curlec's hosted payment page. Never lands users on
    // the dashboard.
    const payload = toQuizPayload(state);
    const encoded = encodePayload(payload);
    const base = LANDING_CTA_URL.replace(/\/+$/, '').replace(/\/register$/, '');
    const refCode = ctaUrl.includes('?ref=') ? new URL(ctaUrl).searchParams.get('ref') : '';
    const search = new URLSearchParams();
    if (refCode) search.set('ref', refCode);
    if (encoded) search.set('intake', encoded);
    window.location.href = `${base}/pay-now${search.toString() ? `?${search.toString()}` : ''}`;
  }

  // ── Welcome / goal ─────────────────────────────────────────────────────────
  if (stepName === 'welcome') {
    return (
      <StepShell
        stepNumber={visibleStepNumber} totalSteps={totalSteps}
        title="What's your main goal?"
        subtitle="We'll use this to personalise your plan with the doctor."
        onBack={onBack} onNext={onNext} nextDisabled={!canAdvance}
      >
        <div className="space-y-3">
          {GOAL_OPTIONS.map((g) => (
            <ChoiceCard
              key={g.value} icon={g.icon} label={g.label}
              active={state.lifestyle.goal === g.value}
              onClick={() => patchLifestyle({ goal: g.value })}
            />
          ))}
        </div>
      </StepShell>
    );
  }

  // ── Basic profile ─────────────────────────────────────────────────────────
  if (stepName === 'basic') {
    return (
      <StepShell
        stepNumber={visibleStepNumber} totalSteps={totalSteps}
        title="What is your date of birth?"
        subtitle="Age helps the doctor assess medical suitability."
        onBack={onBack} onNext={onNext} nextDisabled={!canAdvance}
      >
        <div className="field-wrap">
          <label className="text-xs font-semibold text-ink-600 uppercase tracking-wider">Date of birth</label>
          <DobInput
            value={state.basic.dob}
            onChange={(iso) => patchBasic({ dob: iso })}
          />
        </div>
      </StepShell>
    );
  }

  // ── Under-18 terminal block ───────────────────────────────────────────────
  if (stepName === 'underAgeBlock') {
    const prefill =
      `Hi MyDr.Ai Care Team, I completed the AI Assessment but I am under 18. ` +
      `Please guide me and my parent or legal guardian on the appropriate next step.\n\n` +
      `Assessment reference: ${state.assessmentId || 'N/A'}`;
    return (
      <StepShell
        stepNumber={visibleStepNumber} totalSteps={totalSteps}
        title="You're Under 18"
        onBack={onBack}
        hideNext
      >
        <div className="space-y-4 text-sm text-ink-700 leading-relaxed">
          <p>
            Our current {BRAND_NAME} Weight Loss Programme is designed for adults
            aged 18 and above.
          </p>
          <p>
            For users below 18, please contact our care team on WhatsApp together
            with a parent or legal guardian. We will guide you on the appropriate
            next step.
          </p>
          <div className="pt-2 space-y-3">
            <a
              href={careWhatsappUrl(prefill)}
              target="_blank" rel="noopener noreferrer"
              onClick={() => trackCtaClick({ page: 'assessment_under_18', button_label: 'Contact Our Care Team on WhatsApp', destination: 'whatsapp' })}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3.5 text-sm font-semibold bg-brand-600 text-white hover:bg-brand-700"
            >
              <MessageCircle size={16} /> Contact Our Care Team on WhatsApp
            </a>
            <button
              type="button"
              onClick={() => { clearState(); navigate('/'); }}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3.5 text-sm font-medium border border-ink-200 text-ink-900 hover:bg-ink-50"
            >
              Exit Assessment
            </button>
          </div>
          <p className="text-[11px] text-ink-500 pt-1">
            Assessment reference: <span className="font-mono">{state.assessmentId || '—'}</span>
          </p>
        </div>
      </StepShell>
    );
  }

  // ── Metrics (height / weight / BMI) ───────────────────────────────────────
  if (stepName === 'metrics') {
    return (
      <StepShell
        stepNumber={visibleStepNumber} totalSteps={totalSteps}
        title="Your height & weight"
        subtitle="BMI is one screening signal — not the whole picture. A licensed doctor will review your full profile."
        onBack={onBack} onNext={onNext} nextDisabled={!canAdvance}
      >
        <NumberField label="Height" suffix="cm" value={state.basic.heightCm}
          onChange={(v) => patchBasic({ heightCm: v })} min={100} max={250} />
        <NumberField label="Weight" suffix="kg" value={state.basic.weightKg}
          onChange={(v) => patchBasic({ weightKg: v })} min={30} max={300} inputMode="decimal" />
        {bmi && (
          <div className="mt-2 rounded-xl bg-brand-50 border border-brand-200 px-4 py-3">
            <div className="text-xs uppercase tracking-widest text-brand-700 font-semibold">Your BMI</div>
            <div className="mt-1 text-2xl font-bold text-ink-900">{bmi}</div>
            <div className="text-xs text-ink-600 mt-0.5">
              {bmi < 18.5 ? 'Below the healthy range — please speak with a doctor.' :
                bmi < 25 ? 'Within the healthy range.' :
                bmi < 30 ? 'Overweight range.' :
                bmi < 35 ? 'Obesity (class I).' :
                bmi < 40 ? 'Obesity (class II).' : 'Obesity (class III).'}
            </div>
          </div>
        )}
      </StepShell>
    );
  }

  // ── Low-BMI terminal block ────────────────────────────────────────────────
  if (stepName === 'lowBmiBlock') {
    const prefill =
      `Hi MyDr.Ai Care Team, I completed the AI Assessment and was advised to ` +
      `contact you because my BMI is below the recommended range for the ` +
      `weight-loss programme. Please guide me on the appropriate next step.\n\n` +
      `Assessment reference: ${state.assessmentId || 'N/A'}`;
    return (
      <StepShell
        stepNumber={visibleStepNumber} totalSteps={totalSteps}
        title="Your BMI Is Below the Recommended Range"
        onBack={onBack}
        hideNext
      >
        <div className="space-y-4 text-sm text-ink-700 leading-relaxed">
          <p>
            Not suitable for the online GLP-1 weight-loss pathway — care-team
            review recommended.
          </p>
          <div className="pt-2 space-y-3">
            <a
              href={careWhatsappUrl(prefill)}
              target="_blank" rel="noopener noreferrer"
              onClick={() => trackCtaClick({ page: 'assessment_low_bmi', button_label: 'Contact Our Care Team on WhatsApp', destination: 'whatsapp' })}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3.5 text-sm font-semibold bg-brand-600 text-white hover:bg-brand-700"
            >
              <MessageCircle size={16} /> Contact Our Care Team on WhatsApp
            </a>
            <button
              type="button"
              onClick={() => { clearState(); navigate('/'); }}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3.5 text-sm font-medium border border-ink-200 text-ink-900 hover:bg-ink-50"
            >
              Exit Assessment
            </button>
          </div>
          <p className="text-[11px] text-ink-500 pt-1">
            Assessment reference: <span className="font-mono">{state.assessmentId || '—'}</span>
          </p>
          <p className="text-[10px] text-ink-400 leading-relaxed pt-2">
            This result is based on the information you provided and is not a medical diagnosis.
          </p>
        </div>
      </StepShell>
    );
  }

  // ── Hard contraindications ───────────────────────────────────────────────
  if (stepName === 'hardContras') {
    return (
      <StepShell
        stepNumber={visibleStepNumber} totalSteps={totalSteps}
        title="Medical history"
        subtitle="Please answer honestly. A 'yes' here doesn't mean we can't help — it tells the doctor that medical weight loss medication may not be suitable."
        onBack={onBack} onNext={onNext} nextDisabled={!canAdvance}
      >
        {HARD_CONTRA_Q.map((q) => (
          <YesNoCard key={q.key} label={q.label}
            value={state.contraindications[q.key]}
            onChange={(v) => patchContra({ [q.key]: v })} />
        ))}
      </StepShell>
    );
  }

  // ── High-risk history ─────────────────────────────────────────────────────
  if (stepName === 'highRisk') {
    return (
      <StepShell
        stepNumber={visibleStepNumber} totalSteps={totalSteps}
        title="A few more health questions"
        subtitle="A 'yes' here means a doctor will want a closer look — it does not block you."
        onBack={onBack} onNext={onNext} nextDisabled={!canAdvance}
      >
        {HIGH_RISK_Q.map((q) => (
          <YesNoCard key={q.key} label={q.label}
            value={state.highRiskFlags[q.key]}
            onChange={(v) => patchHighRisk({ [q.key]: v })} />
        ))}
      </StepShell>
    );
  }

  // ── Current medications ──────────────────────────────────────────────────
  if (stepName === 'medications') {
    return (
      <StepShell
        stepNumber={visibleStepNumber} totalSteps={totalSteps}
        title="Current medications"
        subtitle="This helps the doctor screen for drug-interaction safety."
        onBack={onBack} onNext={onNext} nextDisabled={!canAdvance}
      >
        {MEDICATION_Q.map((q) => (
          <YesNoCard key={q.key} label={q.label}
            value={state.medications[q.key]}
            onChange={(v) => patchMeds({ [q.key]: v })} />
        ))}
      </StepShell>
    );
  }

  // ── Challenges multi-select ──────────────────────────────────────────────
  if (stepName === 'challenges') {
    const CHALLENGES = [
      { value: 'CANNOT_LOSE_WEIGHT', label: "I can't seem to lose weight on my own", icon: Frown },
      { value: 'POSTPARTUM_WEIGHT',  label: 'Postpartum weight gain', icon: Baby },
      { value: 'EMOTIONAL_EATING',   label: 'Emotional / stress eating', icon: Cookie },
      { value: 'PLATEAU',            label: "I've hit a plateau", icon: LineChart },
      { value: 'OTHER',              label: 'Something else',     icon: MessageCircle },
    ];
    function toggle(v) {
      const has = state.lifestyle.challenges.includes(v);
      patchLifestyle({
        challenges: has
          ? state.lifestyle.challenges.filter((x) => x !== v)
          : [...state.lifestyle.challenges, v],
      });
    }
    return (
      <StepShell
        stepNumber={visibleStepNumber} totalSteps={totalSteps}
        title="What's holding you back?"
        subtitle="Pick any that apply — your doctor uses this to personalise your plan."
        onBack={onBack} onNext={onNext} nextDisabled={false}
      >
        <div className="space-y-3">
          {CHALLENGES.map((c) => (
            <ChoiceCard key={c.value} icon={c.icon} label={c.label}
              active={state.lifestyle.challenges.includes(c.value)}
              onClick={() => toggle(c.value)} />
          ))}
        </div>
      </StepShell>
    );
  }

  // ── Target weight loss ───────────────────────────────────────────────────
  if (stepName === 'targetLoss') {
    return (
      <StepShell
        stepNumber={visibleStepNumber} totalSteps={totalSteps}
        title="What's your weight-loss goal?"
        subtitle="A rough target helps the doctor plan the right pathway. Actual progress varies by individual."
        onBack={onBack} onNext={onNext} nextDisabled={!canAdvance}
      >
        <NumberField label="Target weight loss" suffix="kg"
          value={state.lifestyle.targetLossKg}
          onChange={(v) => patchLifestyle({ targetLossKg: v })}
          inputMode="decimal" min={1} max={50} />
        <div className="mt-2 grid grid-cols-3 gap-2">
          {[
            { label: '2 – 4 kg',  value: '3' },
            { label: '5 – 8 kg',  value: '6' },
            { label: '9 – 13 kg', value: '10' },
          ].map((b) => (
            <button key={b.value} type="button"
              onClick={() => patchLifestyle({ targetLossKg: b.value })}
              className={[
                'rounded-xl py-3 text-sm font-medium border-2 transition-colors',
                state.lifestyle.targetLossKg === b.value
                  ? 'border-brand-600 bg-brand-50 text-brand-700'
                  : 'border-ink-200 bg-white text-ink-900'
              ].join(' ')}>
              {b.label}
            </button>
          ))}
        </div>
        <p className="mt-4 text-xs text-ink-500">
          These ranges are general progress signals from the published medical
          literature — they are not guarantees. Your doctor will set safe,
          personalised expectations.
        </p>
      </StepShell>
    );
  }

  // ── Eating disorder history ──────────────────────────────────────────────
  if (stepName === 'eatingDisorder') {
    return (
      <StepShell
        stepNumber={visibleStepNumber} totalSteps={totalSteps}
        title="One more question on history"
        subtitle="Have you ever been diagnosed with an eating disorder?"
        onBack={onBack} onNext={onNext} nextDisabled={!canAdvance}
      >
        <YesNoCard
          label="Current or past diagnosis of an eating disorder?"
          sub="Includes anorexia, bulimia or binge-eating disorder."
          value={state.lifestyle.eatingDisorderHistory}
          onChange={(v) => patchLifestyle({ eatingDisorderHistory: v })}
        />
      </StepShell>
    );
  }

  // ── Surgery / anaesthesia (§5) ───────────────────────────────────────────
  if (stepName === 'surgery') {
    return (
      <StepShell
        stepNumber={visibleStepNumber} totalSteps={totalSteps}
        title="Upcoming procedures"
        subtitle="Some weight-loss medications affect how the stomach empties — this matters before procedures with anaesthesia."
        onBack={onBack} onNext={onNext} nextDisabled={!canAdvance}
      >
        {SURGERY_Q.map((q) => (
          <YesNoCard key={q.key} label={q.label}
            value={state.surgery[q.key]}
            onChange={(v) => patchSurgery({ [q.key]: v })} />
        ))}
      </StepShell>
    );
  }

  // ── Urgent / red-flag symptoms (§5) ──────────────────────────────────────
  if (stepName === 'urgent') {
    return (
      <StepShell
        stepNumber={visibleStepNumber} totalSteps={totalSteps}
        title="Symptoms right now"
        subtitle="If any of these are happening, an online assessment is not the right next step — we'll guide you to urgent care."
        onBack={onBack} onNext={onNext} nextDisabled={!canAdvance}
      >
        {URGENT_Q.map((q) => (
          <YesNoCard key={q.key} label={q.label}
            value={state.urgentSymptoms[q.key]}
            onChange={(v) => patchUrgent({ [q.key]: v })} />
        ))}
      </StepShell>
    );
  }

  // ── Result routing — four spec categories ────────────────────────────────
  if (stepName === 'result' && result) {
    return <ResultScreen
      result={result}
      bmi={bmi}
      onBack={onBack}
      restart={restart}
      continueToSignup={continueToSignup}
      totalSteps={totalSteps}
    />;
  }

  return null;
}

// ────────────────────────────────────────────────────────────────────────────
// Result UI — four categories
// ────────────────────────────────────────────────────────────────────────────

function ResultScreen({ result, bmi, onBack, restart, continueToSignup, totalSteps }) {
  const cat = result.category;

  // URGENT — stop everything, send to urgent care.
  if (cat === 'urgent') {
    return (
      <StepShell
        stepNumber={totalSteps} totalSteps={totalSteps}
        title="Please seek urgent care now"
        subtitle="Based on what you described, the right next step is immediate medical attention — not an online assessment."
        onBack={onBack}
        hideNext
        footerExtra={
          <div className="mt-8 space-y-3">
            <a
              href="tel:999"
              onClick={() => trackCtaClick({ page: 'assessment_result', button_label: 'Call 999', destination: 'tel:999' })}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3.5 text-base font-semibold bg-rose-600 text-white hover:bg-rose-700"
            >
              <Phone size={18} /> Call 999 (Malaysia)
            </a>
            <a
              href={whatsappUrl('Hi MyDr.Ai, I am experiencing urgent symptoms. Please advise.')}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackCtaClick({ page: 'assessment_result', button_label: 'Talk to MyDr.Ai', destination: 'whatsapp' })}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3.5 text-sm font-medium border border-ink-200 text-ink-900 hover:bg-ink-50"
            >
              <MessageCircle size={16} /> Talk to MyDr.Ai on WhatsApp
            </a>
            <button
              type="button"
              onClick={restart}
              className="block w-full text-center rounded-full px-5 py-3.5 text-sm font-medium text-ink-700 hover:bg-ink-100"
            >
              Retake assessment
            </button>
          </div>
        }
      >
        <div className="rounded-2xl bg-rose-50 border border-rose-200 p-5">
          <div className="flex gap-3">
            <AlertTriangle size={22} className="text-rose-600 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-ink-900">Seek urgent medical care</div>
              <div className="text-sm text-ink-700 mt-1">
                {result.reason}
              </div>
            </div>
          </div>
        </div>
      </StepShell>
    );
  }

  // NOT_SUITABLE — programme not suitable online.
  if (cat === 'not_suitable') {
    return (
      <StepShell
        stepNumber={totalSteps} totalSteps={totalSteps}
        title="This programme may not be suitable for you"
        subtitle={result.reason}
        onBack={onBack}
        hideNext
        footerExtra={
          <div className="mt-8 space-y-3">
            <a
              href={whatsappUrl('Hi MyDr.Ai, I would like to speak to a healthcare provider about my options.')}
              target="_blank" rel="noopener noreferrer"
              onClick={() => trackCtaClick({ page: 'assessment_result', button_label: 'Speak to a Healthcare Provider', destination: 'whatsapp' })}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3.5 text-sm font-semibold bg-brand-600 text-white hover:bg-brand-700"
            >
              <MessageCircle size={16} /> Speak to a Healthcare Provider
            </a>
            <button
              type="button"
              onClick={restart}
              className="block w-full text-center rounded-full px-5 py-3.5 text-sm font-medium text-ink-700 hover:bg-ink-100"
            >
              Retake assessment
            </button>
          </div>
        }
      >
        <div className="rounded-2xl bg-ink-50 border border-ink-200 p-5">
          <div className="flex gap-3">
            <ShieldCheck size={22} className="text-ink-700 shrink-0 mt-0.5" />
            <div className="text-sm text-ink-700">
              We can't responsibly start this programme through an online flow.
              A licensed healthcare provider can help you find the right path.
            </div>
          </div>
        </div>
      </StepShell>
    );
  }

  // DOCTOR_REVIEW — needs a doctor consultation.
  if (cat === 'doctor_review') {
    return (
      <StepShell
        stepNumber={totalSteps} totalSteps={totalSteps}
        title="A doctor needs to review your profile"
        subtitle={result.reason}
        onBack={onBack}
        hideNext
        footerExtra={
          <div className="mt-8 space-y-3">
            <button
              type="button"
              onClick={continueToSignup}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3.5 text-sm font-semibold bg-brand-600 text-white hover:bg-brand-700"
            >
              Create my account &amp; continue <ArrowRight size={16} />
            </button>
            <p className="text-center text-[11px] text-ink-500 leading-snug">
              By continuing you accept our{' '}
              <Link to="/privacy" className="underline hover:text-brand-700">Privacy Policy</Link>
              {' '}and{' '}
              <Link to="/terms" className="underline hover:text-brand-700">Terms of Use</Link>.
            </p>
            <a
              href={whatsappUrl()}
              target="_blank" rel="noopener noreferrer"
              onClick={() => trackCtaClick({ page: 'assessment_result', button_label: 'Talk to MyDr.Ai', destination: 'whatsapp' })}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3.5 text-sm font-medium border border-ink-200 text-ink-900 hover:bg-ink-50"
            >
              <MessageCircle size={16} /> Talk to MyDr.Ai on WhatsApp
            </a>
            <button
              type="button"
              onClick={restart}
              className="block w-full text-center rounded-full px-5 py-3.5 text-sm font-medium text-ink-700 hover:bg-ink-100"
            >
              Retake assessment
            </button>
          </div>
        }
      >
        <div className="rounded-2xl bg-amber-50 border border-amber-200 p-5">
          <div className="flex gap-3">
            <AlertTriangle size={22} className="text-amber-600 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-ink-900">Doctor consultation recommended</div>
              <div className="text-sm text-ink-700 mt-1">
                {bmi && <span>Your BMI is <strong>{bmi}</strong>. </span>}
                A licensed doctor will review your full medical context before any treatment decision.
              </div>
            </div>
          </div>
        </div>
        <div className="mt-5 text-xs text-ink-500 flex items-start gap-2">
          <ShieldCheck size={14} className="text-brand-600 shrink-0 mt-0.5" />
          <span>
            This is a screening result, not a prescription or approval.
            Prescription decisions are made only by a licensed doctor.
          </span>
        </div>
      </StepShell>
    );
  }

  // MAY_BE_SUITABLE — eligible profile, route to payment + booking.
  return (
    <EligibleResult
      bmi={bmi}
      onBack={onBack}
      restart={restart}
      continueToSignup={continueToSignup}
      totalSteps={totalSteps}
    />
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Eligible result — confirmation screen. Shows the flagship programme fee
// and primary CTA "Pay & Book My Consultation" that routes to portal
// registration + Curlec checkout.
// Pricing constants hard-coded per client sign-off pending pricing config.
// ────────────────────────────────────────────────────────────────────────────

const PROGRAMME_FEE_MYR = 1599;
const INSTALMENT_PER_DAY_MYR = 2.19;
const INSTALMENT_PER_MONTH_MYR = 66.63;
const INSTALMENT_MONTHS = 24;

const WHATS_INCLUDED = [
  { icon: Stethoscope,    title: 'Doctor Consultation', body: 'In-person consultation with a licensed doctor' },
  { icon: ClipboardList,  title: 'Personalised Plan',   body: 'Programme guidance designed around your needs' },
  { icon: ShieldCheck,    title: 'Verified Clinics',    body: 'Access to licensed, verified partner clinics' },
  { icon: TrendingUp,     title: 'Progress Support',    body: 'Structured follow-up and progress monitoring' },
];

const WHY_PATIENTS = [
  'Unified programme pricing',
  'Verified clinic network',
  'Clear next steps after assessment',
  'Private, secure experience',
];

function EligibleResult({ bmi, onBack, restart, continueToSignup, totalSteps }) {
  return (
    <StepShell
      stepNumber={totalSteps} totalSteps={totalSteps}
      onBack={onBack}
      hideNext
      footerExtra={
        <div className="mt-6 space-y-3">
          <button
            type="button"
            onClick={continueToSignup}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-4 text-sm sm:text-base font-semibold bg-brand-600 text-white hover:bg-brand-700 shadow-card"
          >
            <Lock size={16} /> Pay &amp; Book My Consultation <ArrowRight size={16} />
          </button>
          <p className="text-center text-[11px] text-ink-500 leading-snug inline-flex items-center justify-center gap-1.5 w-full">
            <Lock size={11} className="text-ink-500" />
            Secure payment. Reserve your consultation slot after payment confirmation.
          </p>
          <p className="text-center text-[11px] text-ink-500 leading-snug">
            By continuing you accept our{' '}
            <Link to="/privacy" className="underline hover:text-brand-700">Privacy Policy</Link>
            {' '}and{' '}
            <Link to="/terms" className="underline hover:text-brand-700">Terms of Use</Link>.
          </p>
          <a
            href={whatsappUrl()}
            target="_blank" rel="noopener noreferrer"
            onClick={() => trackCtaClick({ page: 'assessment_result', button_label: 'Talk to MyDr.Ai', destination: 'whatsapp' })}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-medium border border-ink-200 text-ink-900 hover:bg-ink-50"
          >
            <MessageCircle size={16} /> Talk to MyDr.Ai on WhatsApp
          </a>
          <button
            type="button"
            onClick={restart}
            className="block w-full text-center rounded-full px-5 py-3 text-sm font-medium text-ink-700 hover:bg-ink-100"
          >
            Retake assessment
          </button>
        </div>
      }
    >
      <div className="space-y-4">

        {/* ── Hero result card ─────────────────────────────────────────── */}
        <div className="relative overflow-hidden rounded-2xl bg-white border border-ink-100 shadow-card">
          {/* Background image — wide consultation shot. Subjects sit on the
              right of the source; the left half is naturally white so the
              overlaid text stays legible without a heavy scrim. */}
          <img
            src="/04 Consultation.png"
            alt="Consultation with a licensed doctor"
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover object-right"
            draggable="false"
          />
          {/* Soft left-side fade to guarantee text contrast across breakpoints. */}
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{ background: 'linear-gradient(90deg, rgba(255,255,255,0.96) 0%, rgba(255,255,255,0.85) 45%, rgba(255,255,255,0) 70%)' }}
          />
          <div className="relative p-3 sm:p-6 max-w-[62%] sm:max-w-[60%]">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 border border-brand-100 px-2 sm:px-3 py-0.5 sm:py-1 text-[9px] sm:text-[11px] font-bold uppercase tracking-wider text-brand-700">
              <Sparkles size={10} className="sm:hidden" />
              <Sparkles size={12} className="hidden sm:block" />
              Assessment Result
            </span>
            <h1 className="mt-2 sm:mt-3 text-sm sm:text-2xl font-bold text-ink-900 leading-tight">
              You may be eligible for a doctor-guided weight management programme.
            </h1>
            <p className="mt-2 sm:mt-3 text-[11px] sm:text-sm text-ink-700 leading-snug sm:leading-relaxed">
              Based on your assessment{bmi ? <> (BMI <strong>{bmi}</strong>)</> : null}, the next step is a
              consultation with a licensed doctor at a verified partner clinic. Your
              programme will be personalised around your health needs and
              weight-management goals.
            </p>
            <div className="mt-3 sm:mt-4 flex flex-wrap gap-1.5 sm:gap-2">
              <Chip icon={Stethoscope} label="Doctor-Guided" />
              <Chip icon={Tag}         label="Unified Pricing" />
              <Chip icon={Percent}     label="0% Instalments" />
            </div>
          </div>
        </div>

        {/* ── What's included ──────────────────────────────────────────── */}
        <div className="rounded-2xl bg-brand-50/40 border border-brand-100 p-3 sm:p-6">
          <h2 className="text-sm sm:text-base font-bold text-ink-900">What's included</h2>
          <div className="mt-3 sm:mt-4 grid grid-cols-4 gap-2 sm:gap-4">
            {WHATS_INCLUDED.map(({ icon: Icon, title, body }) => (
              <div key={title} className="text-center">
                <span className="inline-flex items-center justify-center w-8 h-8 sm:w-11 sm:h-11 rounded-full bg-white border border-brand-100 text-brand-600 mb-1.5 sm:mb-2">
                  <Icon size={14} className="sm:hidden" strokeWidth={1.75} />
                  <Icon size={20} className="hidden sm:block" strokeWidth={1.75} />
                </span>
                <div className="text-[10px] sm:text-sm font-bold text-ink-900 leading-tight">{title}</div>
                <div className="mt-0.5 sm:mt-1 text-[9px] sm:text-xs text-ink-600 leading-snug">{body}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Pricing block ────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 rounded-2xl overflow-hidden border border-ink-100 shadow-card">
          <div className="bg-ink-900 text-white p-3 sm:p-6 relative overflow-hidden">
            <div className="relative z-10">
              <div className="text-[10px] sm:text-sm font-semibold text-white/80 leading-tight">One clear programme fee</div>
              <div className="mt-1.5 sm:mt-2 text-[2rem] sm:text-5xl font-extrabold tracking-tight leading-none">
                <span className="text-xs sm:text-xl font-bold mr-1 align-top">MYR</span>
                {PROGRAMME_FEE_MYR.toLocaleString('en-MY')}
              </div>
              <p className="mt-2 sm:mt-3 text-[9px] sm:text-sm text-white/75 leading-snug sm:leading-relaxed">
                Includes consultation booking and a doctor-guided weight management programme pathway.
              </p>
            </div>
            <HeartPlusWatermark />
          </div>
          <div className="bg-white p-3 sm:p-6">
            <div className="text-[10px] sm:text-sm font-bold text-brand-700 leading-tight">Flexible 0% interest instalments</div>
            <div className="mt-1.5 sm:mt-2">
              <div className="text-[9px] sm:text-xs text-ink-500">From</div>
              <div className="text-[1.75rem] sm:text-4xl font-extrabold text-brand-600 leading-none">
                <span className="text-xs sm:text-base font-bold mr-1 align-top">MYR</span>
                {INSTALMENT_PER_DAY_MYR.toFixed(2)}
                <span className="text-[10px] sm:text-sm font-semibold text-ink-500 ml-1">/ day</span>
              </div>
            </div>
            <div className="mt-2 sm:mt-3 inline-block rounded-full bg-brand-50 border border-brand-100 px-2 sm:px-3 py-0.5 sm:py-1 text-[9px] sm:text-xs text-brand-700 font-semibold leading-tight">
              Approx. MYR {INSTALMENT_PER_MONTH_MYR.toFixed(2)} / month for {INSTALMENT_MONTHS} months
            </div>
            <div className="mt-2 sm:mt-3 text-[10px] sm:text-sm text-ink-700 leading-snug">
              <span className="font-semibold">0% interest</span> instalments up to{' '}
              <span className="font-semibold text-brand-700">{INSTALMENT_MONTHS} months</span>
            </div>
            <p className="mt-2 sm:mt-3 text-[9px] sm:text-[11px] text-ink-500 leading-snug sm:leading-relaxed">
              Available for eligible payment methods and participating banks, subject to approval and applicable terms.
            </p>
          </div>
        </div>

        {/* ── Why patients like this ───────────────────────────────────── */}
        <div className="rounded-2xl bg-white border border-ink-100 shadow-card p-3 sm:p-6">
          <div className="flex items-start gap-2.5 sm:gap-4">
            <span className="shrink-0 inline-flex items-center justify-center w-8 h-8 sm:w-11 sm:h-11 rounded-full bg-brand-50 text-brand-600">
              <HeartHandshake size={14} className="sm:hidden" strokeWidth={1.75} />
              <HeartHandshake size={20} className="hidden sm:block" strokeWidth={1.75} />
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-xs sm:text-base font-bold text-ink-900 leading-tight">Why patients like this</div>
              <ul className="mt-2 sm:mt-3 grid grid-cols-2 gap-x-2 sm:gap-x-4 gap-y-1.5 sm:gap-y-2">
                {WHY_PATIENTS.map((label) => (
                  <li key={label} className="flex items-start gap-1.5 sm:gap-2 text-[10px] sm:text-sm text-ink-700 leading-snug">
                    <Check size={12} className="text-brand-600 shrink-0 mt-0.5 sm:hidden" />
                    <Check size={16} className="text-brand-600 shrink-0 mt-0.5 hidden sm:block" />
                    <span>{label}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <p className="text-[11px] text-ink-500 leading-relaxed flex items-start gap-2">
          <ShieldCheck size={12} className="text-brand-600 shrink-0 mt-0.5" />
          <span>
            This result is a screening signal, not a prescription or approval.
            A licensed doctor makes the final decision; medication, if any, is
            dispensed through licensed pharmacy partners.
          </span>
        </p>
      </div>
    </StepShell>
  );
}

function Chip({ icon: Icon, label }) {
  return (
    <span className="inline-flex items-center gap-1 sm:gap-1.5 rounded-full bg-white border border-ink-200 px-1.5 sm:px-2.5 py-0.5 sm:py-1 text-[9px] sm:text-xs font-semibold text-ink-700 whitespace-nowrap">
      <Icon size={10} className="text-brand-600 sm:hidden" />
      <Icon size={12} className="text-brand-600 hidden sm:block" />
      {label}
    </span>
  );
}

// Faint heart+cross watermark tucked into the dark pricing panel.
function HeartPlusWatermark() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 100 100"
      className="absolute -right-4 -bottom-4 w-32 h-32 text-white/5 pointer-events-none"
      fill="currentColor"
    >
      <path d="M50 88 L14 54 a20 20 0 0 1 28 -28 L50 34 L58 26 a20 20 0 0 1 28 28 Z" />
      <rect x="46" y="42" width="8" height="24" rx="1" />
      <rect x="38" y="50" width="24" height="8" rx="1" />
    </svg>
  );
}

